import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, Italic, Underline, Strikethrough, 
  Heading1, Heading2, Heading3, 
  List, ListOrdered, 
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link, Image as ImageIcon, Quote, Code,
  Undo, Redo,
  Edit2, Code2, Eye
} from 'lucide-react';

const RichTextEditor = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = useState('editor'); // 'editor', 'source', 'preview'
  const editorRef = useRef(null);
  
  // Sync external value to editor ONLY on mount or when switching to editor tab
  // (to avoid cursor jumping during active typing)
  useEffect(() => {
    if (editorRef.current && activeTab === 'editor' && document.activeElement !== editorRef.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value, activeTab]);

  const handleInput = (e) => {
    if (onChange) {
      onChange(e.currentTarget.innerHTML);
    }
  };

  const handleTextareaChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      if (onChange) {
        onChange(editorRef.current.innerHTML);
      }
    }
  };

  const handleLink = () => {
    const url = prompt('Enter the link URL:');
    if (url) {
      executeCommand('createLink', url);
    }
  };

  const handleImage = () => {
    const url = prompt('Enter the image URL:');
    if (url) {
      executeCommand('insertImage', url);
    }
  };

  const handleHeading = (level) => {
    executeCommand('formatBlock', `H${level}`);
  };

  const ToolbarButton = ({ icon: Icon, onClick, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="p-1.5 md:p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
    >
      <Icon className="w-4 h-4 md:w-5 md:h-5" />
    </button>
  );

  const ToolbarDivider = () => (
    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
  );

  return (
    <div className="w-full flex flex-col border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500 transition-all shadow-sm">
      
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <button
          type="button"
          onClick={() => setActiveTab('editor')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'editor' 
              ? 'text-orange-600 border-b-2 border-orange-600 bg-white dark:bg-gray-800' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <Edit2 className="w-4 h-4" />
          Editor
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('source')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'source' 
              ? 'text-orange-600 border-b-2 border-orange-600 bg-white dark:bg-gray-800' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <Code2 className="w-4 h-4" />
          HTML Source
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('preview')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'preview' 
              ? 'text-orange-600 border-b-2 border-orange-600 bg-white dark:bg-gray-800' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <Eye className="w-4 h-4" />
          Preview
        </button>
      </div>

      {/* Editor Toolbar (Only show on 'editor' tab) */}
      {activeTab === 'editor' && (
        <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/80">
          <ToolbarButton icon={Undo} title="Undo" onClick={() => executeCommand('undo')} />
          <ToolbarButton icon={Redo} title="Redo" onClick={() => executeCommand('redo')} />
          <ToolbarDivider />
          
          <ToolbarButton icon={Heading1} title="Heading 1" onClick={() => handleHeading(1)} />
          <ToolbarButton icon={Heading2} title="Heading 2" onClick={() => handleHeading(2)} />
          <ToolbarButton icon={Heading3} title="Heading 3" onClick={() => handleHeading(3)} />
          <ToolbarButton icon={Quote} title="Blockquote" onClick={() => executeCommand('formatBlock', 'BLOCKQUOTE')} />
          <ToolbarButton icon={Code} title="Code Block" onClick={() => executeCommand('formatBlock', 'PRE')} />
          <ToolbarDivider />

          <ToolbarButton icon={Bold} title="Bold" onClick={() => executeCommand('bold')} />
          <ToolbarButton icon={Italic} title="Italic" onClick={() => executeCommand('italic')} />
          <ToolbarButton icon={Underline} title="Underline" onClick={() => executeCommand('underline')} />
          <ToolbarButton icon={Strikethrough} title="Strikethrough" onClick={() => executeCommand('strikeThrough')} />
          <ToolbarDivider />

          <ToolbarButton icon={AlignLeft} title="Align Left" onClick={() => executeCommand('justifyLeft')} />
          <ToolbarButton icon={AlignCenter} title="Align Center" onClick={() => executeCommand('justifyCenter')} />
          <ToolbarButton icon={AlignRight} title="Align Right" onClick={() => executeCommand('justifyRight')} />
          <ToolbarButton icon={AlignJustify} title="Justify" onClick={() => executeCommand('justifyFull')} />
          <ToolbarDivider />

          <ToolbarButton icon={List} title="Bullet List" onClick={() => executeCommand('insertUnorderedList')} />
          <ToolbarButton icon={ListOrdered} title="Numbered List" onClick={() => executeCommand('insertOrderedList')} />
          <ToolbarDivider />

          <ToolbarButton icon={Link} title="Insert Link" onClick={handleLink} />
          <ToolbarButton icon={ImageIcon} title="Insert Image" onClick={handleImage} />
        </div>
      )}

      {/* Content Areas */}
      <div className="flex-1 bg-white dark:bg-gray-800 overflow-y-auto" style={{ minHeight: '300px', maxHeight: '600px' }}>
        
        {/* Editor Tab */}
        {activeTab === 'editor' && (
          <div
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            className="w-full h-full p-4 md:p-6 outline-none dark:text-gray-100 blog-content-preview min-h-[300px]"
            suppressContentEditableWarning={true}
          />
        )}

        {/* Source Tab */}
        {activeTab === 'source' && (
          <textarea
            value={value || ''}
            onChange={handleTextareaChange}
            className="w-full h-full min-h-[300px] p-4 font-mono text-sm bg-gray-900 text-green-400 outline-none resize-y"
            placeholder="<!-- Write HTML here... -->"
          />
        )}

        {/* Preview Tab */}
        {activeTab === 'preview' && (
          <div className="p-4 md:p-8 bg-gray-50 dark:bg-gray-900/50 min-h-[300px]">
            <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
              <div 
                className="blog-content-preview dark:text-gray-200" 
                dangerouslySetInnerHTML={{ __html: value || '<p class="text-gray-400 italic">No content to preview.</p>' }} 
              />
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default RichTextEditor;
